import { yupResolver } from '@hookform/resolvers/yup'
import { EApplicantType, EExternalStep, EExternalStepStatus, EFinancingProgram, EOriginSystemId } from '@if/core/types'
import { getNumberFromQueryString } from '@if/core/utils'
import { Box, Grid } from '@mui/material'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useRefreshCreditReport } from '@src/data/api/credit-application/credit-application-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import {
  useBusinessErrors,
  useCurrentLang,
  useFeatureFlags,
  useFinancingConfig,
  useFinancingProgramEnum,
} from '@src/data/selectors/app-config-selectors'
import {
  useAreAllTasksRequiredForCreditCompleted,
  useCurrentCreditApplicationSpecificNorms,
  useIsFinalDecisionPending,
  useIsRefinancing,
} from '@src/data/selectors/credit-selectors'
import {
  useAreAllDocumentsRequiredForCredit,
  useAreAllDocumentsRequiredForFinancing,
} from '@src/data/selectors/document-selectors'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useEffect, useState, type JSX } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { Breadcrumb, InputTextField, PageError, PageSpinner } from '../../components'
import SelectComponent from '../../components/SelectComponent'
import { buildFullCreditApplicationSchema, Constants, CreditApplication, SelectValueListItem } from '../../data/types'
import { CreditReport } from '../../data/types/CreditReportSchema'
import CreditDecisionHistoryDialog from '../ViewCreditApplicationPage/components/creditDecisionHistoryDialog'
import ApplicantInformations from './components/applicantInformations'
import CreditDecisionIceberg from './components/creditDecision'
import CustomerAccount from './components/customerAccount'
import IFiScore from './components/iFiScore'
import NormIceberg from './components/normIceberg'
import ProofOfReleaseSection from './components/proofOfReleaseSection'
import RefinancingSection from './components/refinancingSection'

enum Dialog {
  CreditDecisionHistory = 'creditDecisionHistory',
  None = '',
}

const CreditDecisionPage = (): JSX.Element | null => {
  const { financingProgramId } = useParams<{ financingProgramId: EFinancingProgram }>()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [applicantCreditReport, setApplicantCreditReport] = useState<CreditReport | null>(null)
  const [coAplicantCreditReport, setCoAplicantCreditReport] = useState<CreditReport | null>(null)
  const tabIndex = getNumberFromQueryString(searchParams, 'tabIndex', 1)
  const [refreshCreditReportHook] = useRefreshCreditReport()
  const normsMessage = useCurrentCreditApplicationSpecificNorms()
  const { creditApplication, documents, requiredDocuments, requiredExternalSteps, isLoading } =
    useCreditApplicationData()
  const error = useBusinessErrors()
  const areAllTasksRequiredForCreditCompleted = useAreAllTasksRequiredForCreditCompleted()

  // Get documents from context and use document hooks
  const areAllDocumentsRequiredForCreditApproved = useAreAllDocumentsRequiredForCredit(documents, requiredDocuments)
  const areAllDocumentsRequiredForFinancingApproved = useAreAllDocumentsRequiredForFinancing(
    documents,
    requiredDocuments,
  )

  const user = useUser()
  const finalDecisionIsPending = useIsFinalDecisionPending()
  const shouldDsiplayIFiScore = Constants.financingProgramConfigs[financingProgramId!].useIFiScore
  const isRefinancing = useIsRefinancing()
  const financingConfig = useFinancingConfig(financingProgramId)
  const editDisabled =
    isRefinancing ||
    creditApplication?.editLocked ||
    finalDecisionIsPending ||
    !user?.rights.canEditCreditApp ||
    creditApplication?.loanId != null

  const canApprove = areAllTasksRequiredForCreditCompleted && areAllDocumentsRequiredForCreditApproved
  const {
    register,
    reset,
    formState: { errors },
  } = useForm<CreditApplication>({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: creditApplication!,
    // Forcing type to be backward compatible with the old schema (should be removed in the future)
    resolver: yupResolver(buildFullCreditApplicationSchema({ financingConfig })) as unknown as Resolver<
      CreditApplication,
      unknown,
      CreditApplication
    >,
  })

  function findRequiredStepsByApplicantTypeAndExternalStepId(
    applicantType: EApplicantType,
    externalStepId: EExternalStep,
  ) {
    return requiredExternalSteps.find((element) => {
      return element.applicantType === applicantType && element.externalStepId === externalStepId
    })
  }
  const featureFlags = useFeatureFlags()
  const hasCoapplicant = creditApplication?.coApplicant !== null

  const ApplicantrequiredExternalStep = findRequiredStepsByApplicantTypeAndExternalStepId(
    EApplicantType.Applicant,
    EExternalStep.BankAccount,
  )
  const CoApplicantrequiredExternalStep = hasCoapplicant
    ? findRequiredStepsByApplicantTypeAndExternalStepId(EApplicantType.CoApplicant, EExternalStep.BankAccount)
    : undefined

  const canOverrideDeclinedDecisions =
    user?.rights.canOverrideDeclinedDecisions === true &&
    ApplicantrequiredExternalStep?.status === EExternalStepStatus.Completed &&
    (!hasCoapplicant || CoApplicantrequiredExternalStep?.status === EExternalStepStatus.Completed) &&
    featureFlags.AllowRefusedOverride

  const [openDialog, setOpenDialog] = useState<Dialog>(Dialog.None)

  const lang = useCurrentLang()
  const financingProgramEnum = useFinancingProgramEnum()
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')
  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
    {
      path: `/Applications/${creditApplication?.financingProgramId}/${creditApplication?.id}/view`,
      label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
    },
    { path: '#', label: t('breadcrumbs.decision') },
  ]

  useEffect(() => {
    if (creditApplication) {
      reset(creditApplication)
    }
  }, [creditApplication, reset])

  const refreshCreditReport = (applicant: 'applicant' | 'coApplicant') => {
    if (creditApplication?.id)
      refreshCreditReportHook({
        creditApplicationId: creditApplication.id,
        financingProgramId: creditApplication.financingProgramId,
        isApplicant: applicant === 'applicant',
        versionTag: creditApplication.versionTag!,
        requestOriginId: EOriginSystemId.Backoffice,
      }).catch(reportErrorToConsole)
  }

  const closeDialog = useCallback(() => setOpenDialog(Dialog.None), [setOpenDialog])
  return (
    <>
      <PageSpinner isLoading={isLoading} />
      {!isLoading && creditApplication && (
        <>
          <Breadcrumb trees={breadCrumbs} />
          <PageError errors={error} />
          <Box sx={{ m: 2 }} />

          <Box sx={{ display: 'flex' }}>
            <SelectComponent
              items={
                (financingProgramEnum as unknown as Record<string, { id: string; [lang: string]: string }>)
                  ? Object.values(
                      financingProgramEnum as unknown as Record<string, { id: string; [lang: string]: string }>,
                    ).map((financingProgram) => {
                      const data: SelectValueListItem = {
                        label: financingProgram[lang] || financingProgram.id,
                        value: financingProgram.id,
                      }
                      return data
                    })
                  : []
              }
              label={t('editCreditApplication.financingProgramType')}
              {...register('financingProgramId')}
              error={errors?.financingProgramId}
              disabled
            />
            <InputTextField value={merchant?.name} label={t('common.merchant')} disabled />
          </Box>

          <Grid container textAlign="center" marginBottom={2}>
            {creditApplication.finalCreditDecision && (
              <>
                {shouldDsiplayIFiScore && (
                  <Grid
                    border="1px solid"
                    borderRadius={2}
                    p={4}
                    size={{
                      xs: 12,
                      md: 3,
                    }}
                  >
                    <IFiScore
                      creditApplication={creditApplication}
                      userCanReadTraces={user?.rights.canReadTraces === true}
                    />
                  </Grid>
                )}

                <Grid
                  border="1px solid"
                  borderRadius={2}
                  p={4}
                  size={{
                    xs: 12,
                    md: 3,
                  }}
                >
                  <NormIceberg
                    normDecision={creditApplication.normsCreditDecision}
                    normsMessage={normsMessage}
                    applicationSuggestedAmount={creditApplication.normsCreditDecision?.maxAmountFinanced ?? 0}
                    financingProgramId={creditApplication.financingProgramId}
                  />
                </Grid>
                <Grid
                  border="1px solid"
                  borderRadius={2}
                  p={4}
                  size={{
                    xs: 12,
                    md: 3,
                  }}
                >
                  <CustomerAccount
                    creditApplication={creditApplication}
                    userCanApproveCreditApplication={user?.rights.canMakeDecision === true}
                    editDisabled={editDisabled}
                  />
                </Grid>
                {financingProgramId === EFinancingProgram.Auto && (
                  <Grid
                    border="1px solid"
                    borderRadius={2}
                    p={4}
                    size={{
                      xs: 12,
                      md: 3,
                    }}
                  >
                    <RefinancingSection
                      creditApplication={creditApplication}
                      userCanApproveCreditApplication={user?.rights.canMakeDecision === true}
                    />
                  </Grid>
                )}
                <Grid
                  border="1px solid"
                  borderRadius={2}
                  p={4}
                  size={{
                    xs: 12,
                    md: 3,
                  }}
                >
                  <CreditDecisionIceberg
                    openHistoryDialog={() => setOpenDialog(Dialog.CreditDecisionHistory)}
                    financingProgramId={financingProgramId!}
                    creditApplication={creditApplication}
                    canApprove={canApprove}
                    editDisabled={editDisabled}
                    userCanApproveCreditApplication={user?.rights.canMakeDecision === true}
                    userCanOverrideDeclinedDecisions={canOverrideDeclinedDecisions}
                    areAllDocumentsRequiredForFinancingApproved={areAllDocumentsRequiredForFinancingApproved}
                  />
                </Grid>
                {creditApplication.proofsOfRelease && financingProgramId === EFinancingProgram.Auto && (
                  <Grid
                    border="1px solid"
                    borderRadius={2}
                    p={4}
                    size={{
                      xs: 12,
                      md: 3,
                    }}
                  >
                    <ProofOfReleaseSection
                      creditApplication={creditApplication}
                      applicantCreditReport={applicantCreditReport}
                      coApplicantCreditReport={coAplicantCreditReport}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
          <CreditDecisionHistoryDialog
            open={openDialog === Dialog.CreditDecisionHistory}
            creditApplicationId={creditApplication.id}
            onClose={closeDialog}
            financingProgramId={financingProgramId!}
          />
          <ApplicantInformations
            creditApplication={creditApplication}
            applicantCreditReport={applicantCreditReport}
            coApplicantCreditReport={coAplicantCreditReport}
            setApplicantCreditReport={setApplicantCreditReport}
            setCoApplicantCreditReport={setCoAplicantCreditReport}
            refreshReport={refreshCreditReport}
            selectedTabIndex={tabIndex}
            userCanRefreshCreditReport={user?.rights.canRefreshCreditReport === true}
            editDisabled={editDisabled}
          />
        </>
      )}
    </>
  )
}

export default CreditDecisionPage
